import { mapActions } from 'vuex';
import { uniqBy } from 'lodash';

export default {
  data() {
    return {
      loadingLessons: false,
    };
  },
  computed: {
    allLessonFlashcardStates() {
      return this.$store.getters['flashcardStates/lessonFlashcardStates'];
    },
    lessons() {
      const lessons = this.$store.getters['lessons/lessons'];

      return uniqBy(lessons, 'id')
        .map(lesson => this.getFilteredLesson(lesson))
        .filter(lesson => lesson.flashcardIds.length > 0)
        .sort((a, b) => a.order - b.order);
    },
    firstUnansweredIndex() {
      if (!this.lesson || !this.lesson.flashcardIds) {
        return 0;
      }

      const lessonFlashcardStates = this.allLessonFlashcardStates.filter(
        state => state.lessonId === this.lesson.id,
      );

      const answeredFlashcardIds = lessonFlashcardStates.map(state => state.flashcardId);

      const firstUnansweredIndex = this.lesson.flashcardIds.findIndex(
        flashcardId => !answeredFlashcardIds.includes(flashcardId),
      );

      return firstUnansweredIndex === -1 ? this.lesson.flashcardIds.length : firstUnansweredIndex;
    },
    firstUnansweredFlashcardId() {
      if (!this.lesson || !this.lesson.flashcardIds) {
        return null;
      }
      return this.lesson.flashcardIds[this.firstUnansweredIndex] || null;
    },
  },
  mounted() {
    this.getLessons();
  },
  methods: {
    ...mapActions('lessons', ['fetchLessons', 'createLesson']),
    getLessons() {
      this.loadingLessons = true;
      return this.fetchLessons()
        .then(response => {
          if (response.data.records.length === 0) {
            this.createNewLesson();
          } else if (this.allLessonsCompleted()) {
            this.createNewLesson();
          }
        })
        .finally(() => {
          this.loadingLessons = false;
        });
    },
    getFirstUnansweredFlashcardId(lesson) {
      if (!lesson || !lesson.flashcardIds) {
        return null;
      }

      const lessonFlashcardStates = this.allLessonFlashcardStates.filter(
        state => state.lessonId === lesson.id,
      );
      const answeredFlashcardIds = lessonFlashcardStates.map(state => state.flashcardId);
      const firstUnansweredIndex = lesson.flashcardIds.findIndex(
        flashcardId => !answeredFlashcardIds.includes(flashcardId),
      );

      return firstUnansweredIndex === -1 ? null : lesson.flashcardIds[firstUnansweredIndex];
    },
    allLessonsCompleted() {
      if (!this.lessons || this.lessons.length === 0) {
        return false;
      }

      return this.lessons.every(lesson => this.status(lesson) === 'completed');
    },
    createNewLesson() {
      this.createLesson();
    },
    getFilteredLesson(lesson) {
      if (!lesson) {
        return lesson;
      }

      const productAssociationIds = lesson.productAssociationIds || [];
      const flashcardIds = lesson.flashcardIds || [];
      const learningSession = lesson.learningSession || [];
      const hasProductAssociationId = this.$store?.getters?.['productAssociations/hasId'];

      if (!productAssociationIds.length || !hasProductAssociationId) {
        return lesson;
      }

      const keepIndexes = productAssociationIds
        .map((id, index) => hasProductAssociationId(id) ? index : null)
        .filter(index => index !== null);

      return {
        ...lesson,
        productAssociationIds: keepIndexes.map(index => productAssociationIds[index]),
        flashcardIds: keepIndexes.map(index => flashcardIds[index]),
        learningSession: keepIndexes.map(index => learningSession[index]),
      };
    },
    status(lesson) {
      const filteredLesson = this.getFilteredLesson(lesson);

      const expectedFlashcardIds = filteredLesson?.flashcardIds || [];

      if (expectedFlashcardIds.length === 0) {
        return 'no-flashcards';
      }

      const completedFlashcardIds = new Set(
        this.allLessonFlashcardStates
          .filter(state => state.lessonId === lesson.id)
          .map(state => state.flashcardId),
      );

      if (completedFlashcardIds.size === 0) {
        return 'not-started';
      }

      const allFlashcardsCompleted = expectedFlashcardIds.every(flashcardId =>
        completedFlashcardIds.has(flashcardId),
      );

      return allFlashcardsCompleted ? 'completed' : 'in-progress';
    },
    getLessonNumber(lesson) {
      const match = lesson?.name?.match(/(\d+)$/);
      return match ? parseInt(match[1], 10) : null;
    },
    checkLessonCompletion(lessonId) {
      const lesson = this.lessons.find(lesson => lesson.id === lessonId);
      if (!lesson) return;

      this.$nextTick(() => {
        const lessonStatus = this.status(lesson);
        if (lessonStatus === 'completed') {
          const lessonNumber = this.getLessonNumber(lesson);
          this.$analytics.events.completedLesson(lessonNumber);
        }
      });
    },
  },
};
